import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { base_url } from "../../baseUrl";
import Loader from "../common/GlobalLoader";

const getAuthHeaders = () => {
  const storedUser = localStorage.getItem("user");
  const token = storedUser ? JSON.parse(storedUser).token : null;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
};

const normalizeKey = (name) => String(name || "").trim().toLowerCase();

const ToggleSwitch = ({ checked, onChange, disabled, title }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    title={title}
    onClick={(e) => {
      e.stopPropagation();
      onChange();
    }}
    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    } ${checked ? "bg-red-500" : "bg-gray-200"}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const ManageItems = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [categoryVisibility, setCategoryVisibility] = useState([]);
  const [items, setItems] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [productCategoryFilter, setProductCategoryFilter] = useState("all");
  const [productVisibilityFilter, setProductVisibilityFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState(null);

  const categoryVisibilityMap = useMemo(() => {
    const map = new Map();
    (categoryVisibility || []).forEach((c) => {
      map.set(c.category_key, !!c.portal_hidden);
    });
    return map;
  }, [categoryVisibility]);

  const refreshAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [catRes, visRes, itemsRes] = await Promise.all([
        axios.get(`${base_url}api/zoho/categories`, {
          params: { page: 1, per_page: 2000 },
        }),
        axios.get(`${base_url}api/portal/category-visibility`, getAuthHeaders()),
        axios.get(`${base_url}api/portal/items/manage`, getAuthHeaders()),
      ]);

      setCategories(catRes.data?.categories || []);
      setCategoryVisibility(visRes.data?.categories || []);
      setItems(itemsRes.data?.items || []);
      setSelectedItemIds(new Set());
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allCategoryNames = useMemo(() => {
    const fromZoho = (categories || []).map((c) => c?.name).filter(Boolean);
    const fromItems = (items || [])
      .map((it) => it?.category_name)
      .filter(Boolean);
    return Array.from(new Set([...fromZoho, ...fromItems])).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  }, [categories, items]);

  useEffect(() => {
    if (!selectedCategory && allCategoryNames.length > 0) {
      setSelectedCategory(allCategoryNames[0]);
    }
  }, [allCategoryNames, selectedCategory]);

  const isItemEffectivelyHidden = (it) =>
    it.portal_hidden === true ||
    categoryVisibilityMap.get(normalizeKey(it.category_name || "")) === true;

  const filteredItems = useMemo(() => {
    const q = normalizeKey(search);
    return (items || [])
      .filter((it) => {
        if (productCategoryFilter === "all") return true;
        const cat = String(it.category_name || "Uncategorized");
        return normalizeKey(cat) === normalizeKey(productCategoryFilter);
      })
      .filter((it) => {
        if (productVisibilityFilter === "all") return true;
        const hidden = isItemEffectivelyHidden(it);
        if (productVisibilityFilter === "hidden") return hidden;
        if (productVisibilityFilter === "visible") return !hidden;
        return true;
      })
      .filter((it) => {
        if (!q) return true;
        const name = normalizeKey(it.item_name);
        const id = normalizeKey(it.item_id);
        return name.includes(q) || id.includes(q);
      })
      .sort((a, b) => String(a.item_name || "").localeCompare(String(b.item_name || "")));
  }, [items, productCategoryFilter, productVisibilityFilter, search, categoryVisibilityMap]);

  const filteredVisibleIds = useMemo(
    () => filteredItems.map((it) => it.item_id),
    [filteredItems]
  );

  const allFilteredSelected =
    filteredVisibleIds.length > 0 &&
    filteredVisibleIds.every((id) => selectedItemIds.has(id));

  const someFilteredSelected = filteredVisibleIds.some((id) => selectedItemIds.has(id));

  const toggleSelectItem = (itemId) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredVisibleIds.forEach((id) => next.delete(id));
      } else {
        filteredVisibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleCategoryClick = (name) => {
    setSelectedCategory(name);
    setProductCategoryFilter(name);
  };

  const updateCategoryHidden = async (categoryName, portal_hidden) => {
    const { data } = await axios.put(
      `${base_url}api/portal/category-visibility/${encodeURIComponent(categoryName)}`,
      { portal_hidden: !!portal_hidden },
      getAuthHeaders()
    );

    const updated = data?.category;
    if (updated?.category_key) {
      setCategoryVisibility((prev) => {
        const next = (prev || []).filter((c) => c.category_key !== updated.category_key);
        next.push(updated);
        return next;
      });
    } else {
      await refreshAll();
    }
  };

  const updateItemHidden = async (itemId, portal_hidden) => {
    const { data } = await axios.put(
      `${base_url}api/items/itemid/${encodeURIComponent(itemId)}`,
      { portal_hidden: !!portal_hidden },
      getAuthHeaders()
    );
    const updated = data?.item;
    if (updated?.item_id) {
      setItems((prev) =>
        (prev || []).map((it) => (it.item_id === updated.item_id ? { ...it, ...updated } : it))
      );
    }
    return updated;
  };

  const updateItemsHiddenBulk = async (itemIds, portal_hidden) => {
    const results = await Promise.allSettled(
      itemIds.map((itemId) => updateItemHidden(itemId, portal_hidden))
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      throw new Error(`${failed} of ${itemIds.length} items failed to update`);
    }
    setSelectedItemIds(new Set());
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog) return;
    setSaving(true);
    setError(null);
    try {
      if (confirmDialog.type === "category") {
        await updateCategoryHidden(confirmDialog.name, confirmDialog.portal_hidden);
      } else if (confirmDialog.type === "item") {
        await updateItemHidden(confirmDialog.itemId, confirmDialog.portal_hidden);
      } else if (confirmDialog.type === "bulk") {
        await updateItemsHiddenBulk(confirmDialog.itemIds, confirmDialog.portal_hidden);
      }
      setConfirmDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const isCategoryHidden = (categoryName) =>
    categoryVisibilityMap.get(normalizeKey(categoryName)) === true;

  const viewingHiddenCategory =
    productCategoryFilter !== "all" && isCategoryHidden(productCategoryFilter);

  useEffect(() => {
    if (viewingHiddenCategory) {
      setSelectedItemIds(new Set());
    }
  }, [viewingHiddenCategory]);

  const getConfirmTitle = () => {
    if (!confirmDialog) return "";
    const hiding = confirmDialog.portal_hidden;
    if (confirmDialog.type === "category") {
      return hiding ? "Hide category in portal?" : "Show category in portal?";
    }
    if (confirmDialog.type === "bulk") {
      return hiding
        ? `Hide ${confirmDialog.itemIds.length} products in portal?`
        : `Unhide ${confirmDialog.itemIds.length} products in portal?`;
    }
    return hiding ? "Hide product in portal?" : "Show product in portal?";
  };

  const getConfirmMessage = () => {
    if (!confirmDialog) return "";
    const hiding = confirmDialog.portal_hidden;
    if (confirmDialog.type === "category") {
      return hiding
        ? `"${confirmDialog.name}" and all its products will be hidden from the customer portal.`
        : `"${confirmDialog.name}" will be visible in the portal again. Individual product settings still apply.`;
    }
    if (confirmDialog.type === "bulk") {
      return hiding
        ? "The selected products will be hidden from the customer portal."
        : "The selected products will be unhidden at the product level. Items in a hidden category stay hidden until that category is shown.";
    }
    return hiding
      ? `"${confirmDialog.itemName}" will be hidden from the customer portal.`
      : `"${confirmDialog.itemName}" will be visible in the customer portal.`;
  };

  return (
    <div className="relative">
      {(loading || saving) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg z-40">
          <Loader />
        </div>
      )}

      <div
        className={`bg-white border rounded-xl p-4 sm:p-6 ${loading || saving ? "opacity-30 pointer-events-none select-none" : ""}`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold theme-text-primary">Manage Items</h2>
            <div className="text-sm text-gray-600 mt-1">
              Toggle on = hidden in portal (category overrides item).
            </div>
          </div>

          <button
            onClick={refreshAll}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm w-full sm:w-auto"
            type="button"
          >
            Refresh
          </button>
        </div>

        {error && <div className="mt-4 text-sm text-red-600">{error}</div>}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Categories */}
          <div className="border rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 text-sm font-semibold text-gray-700">
              Categories
            </div>
            <div className="p-3 max-h-[520px] overflow-y-auto space-y-2">
              {allCategoryNames.length === 0 ? (
                <div className="text-sm text-gray-500">No categories found</div>
              ) : (
                allCategoryNames.map((name) => {
                  const key = normalizeKey(name);
                  const hidden = categoryVisibilityMap.get(key) === true;
                  const isSelected = normalizeKey(selectedCategory) === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleCategoryClick(name)}
                      className={`w-full text-left px-3 py-2 rounded-md border flex items-center justify-between gap-3 ${
                        isSelected ? "bg-indigo-50 border-indigo-200" : "hover:bg-gray-50"
                      }`}
                      title={name}
                    >
                      <span className="text-sm text-gray-800 truncate">{name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-gray-500">{hidden ? "Hidden" : "Visible"}</span>
                        <ToggleSwitch
                          checked={hidden}
                          onChange={() =>
                            setConfirmDialog({
                              type: "category",
                              name,
                              portal_hidden: !hidden,
                            })
                          }
                          title={hidden ? "Show category in portal" : "Hide category in portal"}
                        />
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Products — all items */}
          <div className="lg:col-span-2 border rounded-lg overflow-hidden flex flex-col">
            <div className="px-4 py-3 bg-gray-50 flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm font-semibold text-gray-700">
                  All Products
                  <span className="ml-2 font-normal text-gray-500">
                    ({filteredItems.length}
                    {productCategoryFilter !== "all" ? ` in ${productCategoryFilter}` : ""})
                  </span>
                </div>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search all products..."
                  className="border rounded-md px-3 py-2 text-sm w-full sm:w-64 focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <label htmlFor="product-category-filter" className="text-xs text-gray-500 shrink-0">
                      Category
                    </label>
                    <select
                      id="product-category-filter"
                      value={productCategoryFilter}
                      onChange={(e) => setProductCategoryFilter(e.target.value)}
                      className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 min-w-[140px]"
                    >
                      <option value="all">All categories</option>
                      {allCategoryNames.map((name) => (
                        <option key={normalizeKey(name)} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="product-visibility-filter" className="text-xs text-gray-500 shrink-0">
                      Status
                    </label>
                    <select
                      id="product-visibility-filter"
                      value={productVisibilityFilter}
                      onChange={(e) => setProductVisibilityFilter(e.target.value)}
                      className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 min-w-[120px]"
                    >
                      <option value="all">All</option>
                      <option value="visible">Visible</option>
                      <option value="hidden">Hidden</option>
                    </select>
                  </div>
                  {viewingHiddenCategory && (
                    <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 border border-red-100">
                      Category hidden
                    </span>
                  )}
                </div>

                {!viewingHiddenCategory && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={selectedItemIds.size === 0}
                      onClick={() =>
                        setConfirmDialog({
                          type: "bulk",
                          itemIds: Array.from(selectedItemIds),
                          portal_hidden: true,
                        })
                      }
                      className="px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Hide selected ({selectedItemIds.size})
                    </button>
                    <button
                      type="button"
                      disabled={selectedItemIds.size === 0}
                      onClick={() =>
                        setConfirmDialog({
                          type: "bulk",
                          itemIds: Array.from(selectedItemIds),
                          portal_hidden: false,
                        })
                      }
                      className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Unhide selected ({selectedItemIds.size})
                    </button>
                  </div>
                )}
              </div>

              {viewingHiddenCategory && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <span className="font-medium">{productCategoryFilter}</span> is hidden in the
                  portal. Turn the category toggle to <span className="font-medium">Visible</span> on
                  the left to manage individual products, or pick another category.
                </div>
              )}
            </div>

            {filteredItems.length > 0 && !viewingHiddenCategory && (
              <div className="px-4 py-2 border-b bg-white flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
                  }}
                  onChange={toggleSelectAllFiltered}
                  className="form-checkbox h-4 w-4"
                  title="Select all visible products"
                />
                <span className="text-xs text-gray-500">Select all in view</span>
              </div>
            )}

            <div className="divide-y max-h-[520px] overflow-y-auto flex-1">
              {filteredItems.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No products found.</div>
              ) : (
                filteredItems.map((it) => {
                  const catHidden = isCategoryHidden(it.category_name || "");
                  const itemHidden = it.portal_hidden === true;
                  const effectivelyHidden = isItemEffectivelyHidden(it);
                  const isSelected = selectedItemIds.has(it.item_id);
                  return (
                    <div
                      key={it.item_id}
                      className={`px-4 py-3 flex items-center gap-3 ${!viewingHiddenCategory && isSelected ? "bg-indigo-50/50" : ""}`}
                    >
                      {!viewingHiddenCategory && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectItem(it.item_id)}
                          className="form-checkbox h-4 w-4 shrink-0"
                          title="Select for bulk actions"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {it.item_name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                          <span>ID: {it.item_id}</span>
                          {it.category_name && (
                            <span className="text-gray-400">· {it.category_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {viewingHiddenCategory || catHidden ? (
                          <span className="text-xs font-medium text-red-600">Hidden via category</span>
                        ) : (
                          <>
                            <span className="text-xs text-gray-500 hidden sm:inline">
                              {effectivelyHidden ? "Hidden" : "Visible"}
                            </span>
                            <ToggleSwitch
                              checked={effectivelyHidden}
                              onChange={() =>
                                setConfirmDialog({
                                  type: "item",
                                  itemId: it.item_id,
                                  itemName: it.item_name,
                                  portal_hidden: !itemHidden,
                                })
                              }
                              title={
                                itemHidden
                                  ? "Show product in portal"
                                  : "Hide product in portal"
                              }
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {confirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900">{getConfirmTitle()}</h3>
            <p className="text-sm text-gray-600 mt-2">{getConfirmMessage()}</p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setConfirmDialog(null)}
                disabled={saving}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                disabled={saving}
                className={`px-4 py-2.5 text-sm font-medium text-white rounded-md disabled:opacity-50 w-full sm:w-auto ${
                  confirmDialog.portal_hidden
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {saving
                  ? "Saving..."
                  : confirmDialog.portal_hidden
                    ? "Yes, hide"
                    : confirmDialog.type === "bulk"
                      ? "Yes, unhide"
                      : "Yes, show"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageItems;
